import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ClipboardList, Plus, Trash2, Save, CheckCircle2, XCircle, FileDown, Loader2,
  ArrowLeft, Wand2, User, Building2, Calendar, Euro, AlertTriangle, Send, RefreshCw,
  ChevronDown, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadBlob } from '../utils/download';
import { ConfirmButton } from '../components/ConfirmButton';
import { useAuth } from '../context/AuthContext';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const STATUS_CONFIG = {
  a_remplir_artisan: { label: "À remplir par l'artisan", color: '#71717A', bg: 'bg-zinc-700/20 text-zinc-300 border-zinc-600' },
  a_valider:         { label: 'À valider',              color: '#F59E0B', bg: 'bg-amber-500/15 text-amber-400 border-amber-500/40' },
  valide:            { label: 'Validé',                 color: '#10B981', bg: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40' },
  refuse:            { label: 'Refusé',                 color: '#EF4444', bg: 'bg-red-500/15 text-red-400 border-red-500/40' },
  annule:            { label: 'Annulé',                 color: '#52525B', bg: 'bg-zinc-800 text-zinc-500 border-zinc-700' },
};

const UNIT_OPTIONS = [
  { value: 'u', label: 'u' },
  { value: 'm2', label: 'm²' },
  { value: 'ml', label: 'ml' },
  { value: 'forfait', label: 'forfait' },
  { value: 'h', label: 'h' },
  { value: 'km', label: 'km' },
  { value: 'cm', label: 'cm' },
  { value: 'kg', label: 'kg' },
  { value: 'm3', label: 'm³' },
];

const fmt = (v) => (v ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ================= BDC INVOICES (validated BDCs only) =================

const BdcInvoices = ({ bdcId, totalTtc, onChanged }) => {
  const [invoices, setInvoices] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [montant, setMontant] = useState('');
  const [description, setDescription] = useState('');
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/bons-commande/${bdcId}/invoices`, { withCredentials: true });
      setInvoices(res.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (bdcId) load(); }, [bdcId]); // eslint-disable-line

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.set('file', f);
      const params = new URLSearchParams();
      if (montant) params.set('montant', String(Number(montant)));
      if (description) params.set('description', description);
      await axios.post(`${API}/bons-commande/${bdcId}/invoice?${params.toString()}`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Facture ajoutée · dépense créée dans Suivi Travaux');
      setMontant(''); setDescription('');
      load(); onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const totalPaid = invoices.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  return (
    <div className="mt-4 p-4 rounded-lg border border-emerald-500/20 bg-emerald-500/5" data-testid="bdc-invoices-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-white flex items-center gap-2">
            <FileDown className="w-4 h-4 text-emerald-400" /> Factures liées au BDC
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            Une facture uploadée ici crée automatiquement une dépense dans le Suivi Travaux.
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="flex-1 min-w-[120px]">
          <Label className="text-[10px] text-zinc-500 uppercase tracking-wider">Montant (€ TTC)</Label>
          <Input type="number" step="0.01" value={montant} onChange={(e) => setMontant(e.target.value)}
                 placeholder={`Défaut : ${fmt(totalTtc)}`} className="bg-zinc-950 border-zinc-800 text-white h-9"
                 data-testid="bdc-invoice-amount" />
        </div>
        <div className="flex-[2] min-w-[180px]">
          <Label className="text-[10px] text-zinc-500 uppercase tracking-wider">Description</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)}
                 placeholder="ex : Acompte 30%, facture finale..." className="bg-zinc-950 border-zinc-800 text-white h-9"
                 data-testid="bdc-invoice-description" />
        </div>
        <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={handleFile} className="hidden" data-testid="bdc-invoice-file" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="bg-emerald-600 hover:bg-emerald-500 text-white h-9"
                data-testid="bdc-invoice-upload">
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Uploader facture
        </Button>
      </div>
      {invoices.length > 0 ? (
        <div className="space-y-1.5">
          {invoices.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between gap-2 p-2 rounded bg-zinc-950/60 border border-zinc-800 text-xs"
                 data-testid={`bdc-invoice-${inv.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileDown className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                <a href={`${API}/files/${inv.id}?download=1`} target="_blank" rel="noreferrer"
                   className="text-zinc-300 hover:text-white truncate">{inv.filename}</a>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] text-zinc-500">
                  {new Date(inv.uploaded_at).toLocaleDateString('fr-FR')}
                </span>
                <span className="font-mono text-emerald-400 text-xs">{fmt(Number(inv.amount) || 0)} €</span>
              </div>
            </div>
          ))}
          <div className="flex justify-between items-center pt-2 mt-2 border-t border-emerald-500/20 text-xs font-mono">
            <span className="text-zinc-500">Total facturé</span>
            <span className="text-emerald-400 font-bold">{fmt(totalPaid)} € / {fmt(totalTtc)} € BDC</span>
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-zinc-500 italic">Aucune facture uploadée pour ce BDC.</p>
      )}
    </div>
  );
};

// ================= BDC DETAIL DIALOG =================

const BdcDetail = ({ bdc, artisans, isArtisan, onClose, onChanged }) => {
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(bdc)));
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refuseMotif, setRefuseMotif] = useState('');
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [hideZeroLines, setHideZeroLines] = useState(true);

  useEffect(() => { setForm(JSON.parse(JSON.stringify(bdc))); }, [bdc]);

  // Lock rules :
  //  - validé => personne ne modifie
  //  - pour artisan : lock si status n'est pas draft/refuse (il ne peut agir que sur ses propres drafts)
  const locked = form.status === 'valide'
    || (isArtisan && !['a_remplir_artisan', 'refuse'].includes(form.status));
  const statusCfg = STATUS_CONFIG[form.status] || STATUS_CONFIG.a_remplir_artisan;

  const totals = useMemo(() => {
    const mo = (form.items || []).reduce((s, it) => s + (Number(it.quantite) || 0) * (Number(it.prix_unitaire) || 0), 0);
    const fo = (form.fournitures || []).reduce((s, f) => s + (Number(f.quantite) || 0) * (Number(f.prix_unitaire) || 0), 0);
    const ht = mo + fo;
    const tvaPct = Number(form.tva_pct) || 0;
    const tva = ht * tvaPct / 100;
    return { mo, fo, ht, tva, ttc: ht + tva };
  }, [form]);

  const updateItem = (arrKey, idx, patch) => {
    setForm((prev) => {
      const arr = [...(prev[arrKey] || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...prev, [arrKey]: arr };
    });
  };

  const addItem = (arrKey) => {
    setForm((prev) => ({
      ...prev,
      [arrKey]: [...(prev[arrKey] || []), { id: Math.random().toString(36).slice(2), libelle: '', unite: 'u', quantite: 0, prix_unitaire: 0, total: 0, notes: '' }],
    }));
  };

  const removeItem = (arrKey, idx) => {
    setForm((prev) => ({ ...prev, [arrKey]: (prev[arrKey] || []).filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = isArtisan
        ? {
            items: form.items,
            fournitures: form.fournitures,
            notes_artisan: form.notes_artisan || '',
            date_debut_prevue: form.date_debut_prevue || null,
            duree_prevue_jours: form.duree_prevue_jours ? Number(form.duree_prevue_jours) : null,
          }
        : {
            items: form.items,
            fournitures: form.fournitures,
            tva_pct: Number(form.tva_pct) || 10,
            artisan_id: form.artisan_id || null,
            artisan_name: form.artisan_name || '',
            date_debut_prevue: form.date_debut_prevue || null,
            duree_prevue_jours: form.duree_prevue_jours ? Number(form.duree_prevue_jours) : null,
            notes_artisan: form.notes_artisan || '',
            notes_mdb: form.notes_mdb || '',
          };
      const res = await axios.put(`${API}/bons-commande/${form.id}`, payload, { withCredentials: true });
      toast.success('Bon de commande enregistré');
      setForm(res.data);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (newStatus, motif = null) => {
    setActing(true);
    try {
      // For transitions that "submit" the BDC (a_valider), always save pending form edits first,
      // otherwise the artisan's corrections would be lost.
      if (newStatus === 'a_valider') {
        await saveQuiet();
      }
      const params = new URLSearchParams({ status: newStatus });
      if (motif) params.set('motif_refus', motif);
      const res = await axios.patch(`${API}/bons-commande/${form.id}/status?${params.toString()}`, {}, { withCredentials: true });
      const labels = { a_valider: 'Envoyé pour validation', valide: 'Validé ✓', refuse: 'Refusé', a_remplir_artisan: 'Réouvert', annule: 'Annulé' };
      toast.success(labels[newStatus] || 'Statut mis à jour');
      setForm(res.data);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setActing(false);
    }
  };

  const saveQuiet = async () => {
    // Save without a success toast (used before status transitions to avoid losing edits).
    try {
      const payload = isArtisan
        ? {
            items: form.items,
            fournitures: form.fournitures,
            notes_artisan: form.notes_artisan || '',
            date_debut_prevue: form.date_debut_prevue || null,
            duree_prevue_jours: form.duree_prevue_jours ? Number(form.duree_prevue_jours) : null,
          }
        : {
            items: form.items,
            fournitures: form.fournitures,
            tva_pct: Number(form.tva_pct) || 10,
            artisan_id: form.artisan_id || null,
            artisan_name: form.artisan_name || '',
            date_debut_prevue: form.date_debut_prevue || null,
            duree_prevue_jours: form.duree_prevue_jours ? Number(form.duree_prevue_jours) : null,
            notes_artisan: form.notes_artisan || '',
            notes_mdb: form.notes_mdb || '',
          };
      const res = await axios.put(`${API}/bons-commande/${form.id}`, payload, { withCredentials: true });
      setForm(res.data);
    } catch (err) {
      // Re-throw so caller knows save failed and doesn't transition the status
      throw err;
    }
  };

  const exportPdf = async () => {
    try {
      const res = await axios.get(`${API}/bons-commande/${form.id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const corps = (form.corps_metier || 'bdc').toLowerCase().replace(/[^\w]/g, '_');
      downloadBlob(res.data, `bdc_${corps}_${form.id.slice(0, 8)}.pdf`, 'application/pdf');
      toast.success('PDF téléchargé');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export échoué');
    }
  };

  const syncBpu = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/bons-commande/${form.id}/sync-bpu`, {}, { withCredentials: true });
      const { updated_prices, added, total_items } = res.data || {};
      toast.success(`Synchro BPU : +${added} ligne(s), ${updated_prices} prix mis à jour (${total_items} au total)`);
      // Refetch
      const r = await axios.get(`${API}/bons-commande/${form.id}`, { withCredentials: true });
      setForm(r.data);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Synchro échouée');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-5xl w-[96vw] max-h-[92vh] overflow-y-auto p-0" data-testid="bdc-detail-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10">
          <div className="flex flex-wrap gap-3 items-start">
            <button onClick={onClose} className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white mt-1" aria-label="Fermer">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-white flex items-center gap-2 flex-wrap">
                <ClipboardList className="w-5 h-5 text-emerald-400" />
                Bon de commande — {form.corps_metier}
                <Badge className={`border ${statusCfg.bg}`} data-testid="bdc-status-badge">{statusCfg.label}</Badge>
              </DialogTitle>
              <p className="text-xs text-zinc-500 mt-1">
                {form.property_title ? `${form.property_title} · ` : ''}
                Référence : {form.id.slice(0, 8).toUpperCase()}
              </p>
              {form.status === 'refuse' && form.motif_refus && (
                <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Motif refus : {form.motif_refus}
                </p>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="px-5 py-4 space-y-5">
          {/* Identity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs">Artisan attitré</Label>
              {isArtisan ? (
                <div className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-200">
                  {form.artisan_name || '—'}
                </div>
              ) : (
                <Select
                  value={form.artisan_id || 'none'}
                  onValueChange={(v) => {
                    if (v === 'none') { setForm((p) => ({ ...p, artisan_id: null, artisan_name: '' })); return; }
                    const a = artisans.find((x) => x.id === v);
                    setForm((p) => ({ ...p, artisan_id: v, artisan_name: a?.name || '' }));
                  }}
                  disabled={locked}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="bdc-artisan-select">
                    <SelectValue placeholder="Sélectionner un artisan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun artisan défini</SelectItem>
                    {artisans.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.metier ? ` · ${a.metier}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Date démarrage prévue</Label>
                <Input type="date" value={form.date_debut_prevue || ''} onChange={(e) => setForm((p) => ({ ...p, date_debut_prevue: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-white" disabled={locked} />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Durée (jours)</Label>
                <Input type="number" value={form.duree_prevue_jours || ''} onChange={(e) => setForm((p) => ({ ...p, duree_prevue_jours: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-white" disabled={locked} />
              </div>
            </div>
          </div>

          {/* Items MO */}
          <section>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-white">Main d'œuvre</h3>
                <button
                  type="button"
                  onClick={() => setHideZeroLines((v) => !v)}
                  className="text-[11px] px-2 py-1 rounded border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors"
                  data-testid="bdc-toggle-zero-lines"
                  title="Masquer les lignes dont la quantité ET le prix unitaire sont à 0"
                >
                  {hideZeroLines ? '👁️ Afficher tout' : '🫥 Masquer les 0 €'}
                </button>
              </div>
              {!locked && (
                <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-300 h-7" onClick={() => addItem('items')} data-testid="bdc-add-item">
                  <Plus className="w-3 h-3 mr-1" /> Ajouter une ligne
                </Button>
              )}
            </div>
            <LineEditor arrKey="items" locked={locked} isArtisan={isArtisan} items={form.items || []} onUpdate={updateItem} onRemove={removeItem} testPrefix="item" hideZero={hideZeroLines} />
          </section>

          {/* Fournitures */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-white">Fournitures / Matériel</h3>
              {!locked && (
                <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-300 h-7" onClick={() => addItem('fournitures')} data-testid="bdc-add-fourniture">
                  <Plus className="w-3 h-3 mr-1" /> Ajouter une fourniture
                </Button>
              )}
            </div>
            <LineEditor arrKey="fournitures" locked={locked} isArtisan={isArtisan} items={form.fournitures || []} onUpdate={updateItem} onRemove={removeItem} testPrefix="fourniture" hideZero={hideZeroLines} />
          </section>

          {/* Totaux */}
          <div className="border-t border-zinc-800 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs">TVA applicable</Label>
              {isArtisan ? (
                <div className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-200">
                  {form.tva_pct ?? 10} %
                </div>
              ) : (
                <Select value={String(form.tva_pct ?? 10)} onValueChange={(v) => setForm((p) => ({ ...p, tva_pct: Number(v) }))} disabled={locked}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white max-w-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0 % (exonéré)</SelectItem>
                    <SelectItem value="5.5">5,5 % (éco-travaux)</SelectItem>
                    <SelectItem value="10">10 % (rénovation +2 ans)</SelectItem>
                    <SelectItem value="20">20 % (neuf / standard)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="bg-zinc-950 rounded-lg border border-zinc-800 p-3 text-xs space-y-1 font-mono">
              <div className="flex justify-between"><span className="text-zinc-500">Main d'œuvre HT</span><span className="text-zinc-200">{fmt(totals.mo)} €</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Fournitures HT</span><span className="text-zinc-200">{fmt(totals.fo)} €</span></div>
              <div className="flex justify-between border-t border-zinc-800 pt-1 font-bold"><span className="text-white">Total HT</span><span className="text-white">{fmt(totals.ht)} €</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">TVA {form.tva_pct} %</span><span className="text-zinc-300">{fmt(totals.tva)} €</span></div>
              <div className="flex justify-between border-t border-emerald-500/30 pt-1 text-emerald-400 text-sm font-bold"><span>Total TTC</span><span data-testid="bdc-total-ttc">{fmt(totals.ttc)} €</span></div>
            </div>
          </div>

          {/* Comparatif Visite Technique vs BDC */}
          {Number(form.visit_estimate) > 0 && (
            (() => {
              const est = Number(form.visit_estimate) || 0;
              const diff = totals.ht - est;
              const pct = est > 0 ? (diff / est) * 100 : 0;
              let color, bg, border, Icon, label;
              if (Math.abs(pct) <= 10) {
                color = 'text-emerald-400'; bg = 'bg-emerald-500/5'; border = 'border-emerald-500/30'; label = 'Conforme à l\'estimation'; Icon = CheckCircle2;
              } else if (pct > 10 && pct <= 25) {
                color = 'text-amber-400'; bg = 'bg-amber-500/5'; border = 'border-amber-500/30'; label = 'Écart modéré'; Icon = AlertTriangle;
              } else {
                color = 'text-red-400'; bg = 'bg-red-500/5'; border = 'border-red-500/30'; label = pct > 0 ? 'Dépassement important' : 'Sous l\'estimation'; Icon = AlertTriangle;
              }
              return (
                <div className={`rounded-lg border p-4 ${bg} ${border}`} data-testid="bdc-variance-card">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${color}`} />
                      <div>
                        <div className={`text-sm font-semibold ${color}`}>Comparatif avec l'estimation de visite</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">{label}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs font-mono">
                      <div className="text-right">
                        <div className="text-zinc-500 text-[10px] uppercase tracking-wide">Estimation visite</div>
                        <div className="text-zinc-300">{fmt(est)} €</div>
                      </div>
                      <div className="text-right">
                        <div className="text-zinc-500 text-[10px] uppercase tracking-wide">BDC actuel (HT)</div>
                        <div className="text-white">{fmt(totals.ht)} €</div>
                      </div>
                      <div className="text-right">
                        <div className="text-zinc-500 text-[10px] uppercase tracking-wide">Écart</div>
                        <div className={`${color} font-bold text-sm`} data-testid="bdc-variance-pct">
                          {diff >= 0 ? '+' : ''}{fmt(diff)} € ({pct >= 0 ? '+' : ''}{pct.toFixed(1)}%)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()
          )}

          {/* Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs">Notes de l'artisan</Label>
              <Textarea value={form.notes_artisan || ''} onChange={(e) => setForm((p) => ({ ...p, notes_artisan: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-white min-h-[80px]" disabled={locked} placeholder="Délais, contraintes, observations..." />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Notes Maître d'ouvrage</Label>
              <Textarea
                value={form.notes_mdb || ''}
                onChange={(e) => setForm((p) => ({ ...p, notes_mdb: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-white min-h-[80px]"
                disabled={locked || isArtisan}
                placeholder={isArtisan ? '' : 'Contraintes du chantier, accès, etc.'}
              />
            </div>
          </div>

          {/* Invoices (only when BDC validated) : upload crée une dépense auto dans travaux */}
          {form.status === 'valide' && !isArtisan && (
            <BdcInvoices bdcId={form.id} totalTtc={totals.ttc} onChanged={onChanged} />
          )}
        </div>

        {/* Action bar */}
        <div className="px-5 py-3 border-t border-zinc-800 flex flex-wrap gap-2 justify-between sticky bottom-0 bg-zinc-900">
          <div className="flex gap-2 flex-wrap">
            {form.status === 'a_remplir_artisan' && (
              <Button variant="outline" className="border-amber-600/50 text-amber-400 hover:bg-amber-500/10" onClick={() => changeStatus('a_valider')} disabled={acting} data-testid="bdc-submit-for-validation">
                <Send className="w-4 h-4 mr-1" /> {isArtisan ? "J'ai fini, soumettre pour validation" : "L'artisan a fini — à valider"}
              </Button>
            )}
            {form.status === 'refuse' && isArtisan && (
              <Button variant="outline" className="border-amber-600/50 text-amber-400 hover:bg-amber-500/10" onClick={() => changeStatus('a_valider')} disabled={acting} data-testid="bdc-resubmit">
                <Send className="w-4 h-4 mr-1" /> Corrections effectuées, re-soumettre
              </Button>
            )}
            {!isArtisan && form.status === 'a_valider' && (
              <>
                <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => changeStatus('valide')} disabled={acting} data-testid="bdc-validate">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Valider
                </Button>
                <Button variant="outline" className="border-red-600/50 text-red-400 hover:bg-red-500/10" onClick={() => setRefuseOpen(true)} disabled={acting} data-testid="bdc-refuse">
                  <XCircle className="w-4 h-4 mr-1" /> Refuser
                </Button>
              </>
            )}
            {!isArtisan && form.status === 'refuse' && (
              <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => changeStatus('a_remplir_artisan')} disabled={acting} data-testid="bdc-reopen">
                <RefreshCw className="w-4 h-4 mr-1" /> Réouvrir pour l'artisan
              </Button>
            )}
            {form.status === 'valide' && (
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={exportPdf} data-testid="bdc-export-pdf">
                <FileDown className="w-4 h-4 mr-1" /> Exporter PDF
              </Button>
            )}
            {!isArtisan && form.status !== 'valide' && (
              <Button
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                onClick={syncBpu}
                disabled={syncing}
                data-testid="bdc-sync-bpu"
                title="Récupère les dernières lignes & prix du Bordereau (sans toucher aux quantités déjà saisies)"
              >
                {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Sync BPU
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {!locked && (
              <Button onClick={save} disabled={saving} className="bg-zinc-100 text-zinc-900 hover:bg-white" data-testid="bdc-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Enregistrer
              </Button>
            )}
            <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={onClose}>Fermer</Button>
          </div>
        </div>

        {/* Refuse motif dialog */}
        <Dialog open={refuseOpen} onOpenChange={setRefuseOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
            <DialogHeader><DialogTitle>Motif du refus</DialogTitle></DialogHeader>
            <Textarea value={refuseMotif} onChange={(e) => setRefuseMotif(e.target.value)} className="bg-zinc-950 border-zinc-800 text-white min-h-[100px]" placeholder="Décrivez précisément le motif (prix, quantités, descriptif...)" data-testid="bdc-refuse-motif" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => setRefuseOpen(false)}>Annuler</Button>
              <Button className="bg-red-600 hover:bg-red-500 text-white" onClick={() => { changeStatus('refuse', refuseMotif); setRefuseOpen(false); setRefuseMotif(''); }} data-testid="bdc-confirm-refuse">
                Confirmer le refus
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

const LineEditor = ({ arrKey, items, locked, onUpdate, onRemove, testPrefix, isArtisan = false, hideZero = false }) => {
  if (!items || items.length === 0) {
    return <p className="text-xs text-zinc-600 italic bg-zinc-950 border border-dashed border-zinc-800 rounded p-3 text-center">Aucune ligne — ajoutez-en une.</p>;
  }
  // Mémorise l'index d'origine pour que les modifications/suppressions ciblent la bonne ligne,
  // même quand on masque les lignes à 0 €.
  const visibleEntries = items
    .map((it, originalIdx) => ({ it, originalIdx }))
    .filter(({ it }) => {
      if (!hideZero) return true;
      const total = (Number(it.quantite) || 0) * (Number(it.prix_unitaire) || 0);
      return total > 0;
    });
  const hiddenCount = items.length - visibleEntries.length;
  // Artisan : no libellé/unité/prix edits (sauf sur fournitures qu'il ajoute), pas de suppression des lignes BPU
  const artisanCanEditLine = (it) => !it.bordereau_item_id;
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden">
      <div className="grid grid-cols-12 gap-1 px-2 py-1.5 bg-zinc-900 text-[10px] uppercase tracking-wider text-zinc-500">
        <div className="col-span-6">Désignation</div>
        <div className="col-span-1 text-center">Unité</div>
        <div className="col-span-1 text-right">Qté</div>
        <div className="col-span-2 text-right">P.U. HT</div>
        <div className="col-span-1 text-right">Total</div>
        <div className="col-span-1 text-center"></div>
      </div>
      {visibleEntries.length === 0 && (
        <p className="text-xs text-zinc-600 italic px-3 py-3 text-center">
          Toutes les lignes sont à 0 € — désactivez le filtre pour les afficher.
        </p>
      )}
      {visibleEntries.map(({ it, originalIdx }) => {
        const idx = originalIdx;
        const total = (Number(it.quantite) || 0) * (Number(it.prix_unitaire) || 0);
        const canEditThisLine = isArtisan ? artisanCanEditLine(it) : !locked;
        const lockLabel = locked || (isArtisan && !canEditThisLine);
        return (
          <div key={it.id || idx} className="grid grid-cols-12 gap-1 px-2 py-1 items-center border-t border-zinc-800/60">
            <Input
              value={it.libelle || ''}
              onChange={(e) => onUpdate(arrKey, idx, { libelle: e.target.value })}
              disabled={lockLabel}
              placeholder="Libellé de la prestation"
              className="col-span-6 bg-zinc-900 border-zinc-800 text-white h-7 text-xs"
              data-testid={`bdc-${testPrefix}-libelle-${idx}`}
            />
            <Select value={it.unite || 'u'} onValueChange={(v) => onUpdate(arrKey, idx, { unite: v })} disabled={lockLabel}>
              <SelectTrigger className="col-span-1 bg-zinc-900 border-zinc-800 text-white h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {UNIT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              type="number" step="0.01"
              value={it.quantite ?? ''}
              onChange={(e) => onUpdate(arrKey, idx, { quantite: e.target.value })}
              disabled={locked}
              className={`col-span-1 bg-zinc-900 border-zinc-800 h-7 text-xs text-right font-mono ${isArtisan ? 'text-amber-300 ring-1 ring-amber-500/30' : 'text-white'}`}
              data-testid={`bdc-${testPrefix}-qty-${idx}`}
            />
            <Input
              type="number" step="0.01"
              value={it.prix_unitaire ?? ''}
              onChange={(e) => onUpdate(arrKey, idx, { prix_unitaire: e.target.value })}
              disabled={lockLabel}
              className="col-span-2 bg-zinc-900 border-zinc-800 text-white h-7 text-xs text-right font-mono"
              data-testid={`bdc-${testPrefix}-pu-${idx}`}
            />
            <div className="col-span-1 text-right text-xs font-mono text-emerald-400">{fmt(total)}</div>
            <div className="col-span-1 flex justify-center">
              {!locked && (!isArtisan || !it.bordereau_item_id) && (
                <button onClick={() => onRemove(arrKey, idx)} className="text-zinc-500 hover:text-red-400 p-1" title="Supprimer">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        );
      })}
      {hideZero && hiddenCount > 0 && (
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-zinc-600 bg-zinc-900/50 border-t border-zinc-800 text-center">
          {hiddenCount} ligne{hiddenCount > 1 ? 's' : ''} à 0 € masquée{hiddenCount > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

// ================= MAIN LIST PAGE =================

export const BonsCommandePage = () => {
  const { user } = useAuth();
  const isArtisan = user?.role === 'artisan';
  const [bdcs, setBdcs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filterProject, setFilterProject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [hideEmpty, setHideEmpty] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const calls = [axios.get(`${API}/bons-commande`, { withCredentials: true })];
      if (!isArtisan) {
        calls.push(axios.get(`${API}/projects`, { withCredentials: true }));
        calls.push(axios.get(`${API}/artisans`, { withCredentials: true }));
      }
      const results = await Promise.all(calls);
      setBdcs(results[0].data || []);
      if (!isArtisan) {
        setProjects(results[1].data || []);
        setArtisans(results[2].data || []);
      }
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Deep link : ?bdc_id=...
  useEffect(() => {
    const bid = searchParams.get('bdc_id');
    if (bid && bdcs.length > 0) {
      const b = bdcs.find((x) => x.id === bid);
      if (b) setSelected(b);
      searchParams.delete('bdc_id');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bdcs]);

  const activeProjects = useMemo(() => projects.filter((p) => ['achete', 'en_renovation', 'en_vente'].includes(p.status)), [projects]);

  const handleAutoGenerate = async () => {
    if (filterProject === 'all') { toast.error("Sélectionnez d'abord un projet"); return; }
    setAutoGenerating(true);
    try {
      const res = await axios.post(`${API}/projects/${filterProject}/bons-commande/auto-generate`, {}, { withCredentials: true });
      const n = res.data?.created || 0;
      if (n > 0) toast.success(`${n} bon${n > 1 ? 's' : ''} de commande créé${n > 1 ? 's' : ''}`);
      else toast.info("Aucun BDC à créer (déjà existants ou pas de visite technique chiffrée)");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur génération');
    } finally {
      setAutoGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/bons-commande/${id}`, { withCredentials: true });
      toast.success('Bon de commande supprimé');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const filtered = useMemo(() => bdcs.filter((b) => {
    if (filterProject !== 'all' && b.project_id !== filterProject) return false;
    if (filterStatus !== 'all' && b.status !== filterStatus) return false;
    if (hideEmpty && Number(b.total_ttc || 0) === 0 && Number(b.total_ht || 0) === 0) return false;
    return true;
  }), [bdcs, filterProject, filterStatus, hideEmpty]);

  // Regroupement par projet (property_title ou project_id)
  const groupedByProject = useMemo(() => {
    const groups = {};
    filtered.forEach((b) => {
      const key = b.project_id || '_no_project';
      if (!groups[key]) {
        groups[key] = {
          project_id: b.project_id,
          title: b.property_title || (b.project_id ? `Projet ${b.project_id.slice(0, 8)}` : 'Sans projet'),
          city: b.property_city || '',
          items: [],
          total_ttc: 0,
        };
      }
      groups[key].items.push(b);
      groups[key].total_ttc += Number(b.total_ttc || 0);
    });
    // Sort groups : by project title alpha
    return Object.values(groups).sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [filtered]);

  const toggleGroup = (key) => setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const stats = useMemo(() => {
    const out = { total: bdcs.length, a_remplir_artisan: 0, a_valider: 0, valide: 0, refuse: 0 };
    bdcs.forEach((b) => { out[b.status] = (out[b.status] || 0) + 1; });
    return out;
  }, [bdcs]);

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="bons-commande-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-emerald-400" /> Bons de commande
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            {isArtisan
              ? 'Remplissez les quantités et soumettez pour validation.'
              : 'Chaque artisan remplit son bon, vous validez avant lancement des travaux.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={fetchAll} data-testid="refresh-bdcs">
            <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
          </Button>
          {!isArtisan && (
            <Button
              variant="outline"
              className="border-emerald-600/50 text-emerald-400 hover:bg-emerald-500/10"
              onClick={handleAutoGenerate}
              disabled={autoGenerating || filterProject === 'all'}
              data-testid="auto-generate-bdcs"
              title={filterProject === 'all' ? 'Sélectionnez un projet d\'abord' : 'Régénère les BDC manquants à partir de la visite'}
            >
              {autoGenerating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
              Générer depuis visite
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { k: 'total', label: 'Total', color: '#A1A1AA' },
          { k: 'a_remplir_artisan', label: "À remplir", color: '#71717A' },
          { k: 'a_valider', label: 'À valider', color: '#F59E0B' },
          { k: 'valide', label: 'Validés', color: '#10B981' },
          { k: 'refuse', label: 'Refusés', color: '#EF4444' },
        ].map((s) => (
          <Card key={s.k} className="bg-zinc-900 border-zinc-800" data-testid={`bdc-stat-${s.k}`}>
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{s.label}</div>
              <div className="text-2xl font-bold font-mono" style={{ color: s.color }}>{stats[s.k] || 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-3 flex flex-wrap gap-3 items-end">
          {!isArtisan && (
            <div className="flex-1 min-w-[200px]">
              <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Projet</Label>
              <Select value={filterProject} onValueChange={setFilterProject}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-9" data-testid="filter-project"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les projets</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.property_title || p.id.slice(0, 8)} {p.property_city ? `· ${p.property_city}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex-1 min-w-[200px]">
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">Statut</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-md border border-zinc-800 bg-zinc-950 hover:border-zinc-700" data-testid="toggle-hide-empty">
              <input
                type="checkbox"
                checked={hideEmpty}
                onChange={(e) => setHideEmpty(e.target.checked)}
                className="accent-emerald-500"
              />
              <span className="text-xs text-zinc-300">Masquer les BDC à 0 €</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="text-center text-zinc-500 py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 border-dashed">
          <CardContent className="p-8 text-center text-zinc-500">
            <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Aucun bon de commande.</p>
            <p className="text-xs mt-1">Les BDC sont créés automatiquement quand un projet passe en « Acheté » ou « En rénovation ».</p>
            {filterProject !== 'all' && (
              <Button className="mt-4 bg-emerald-600 hover:bg-emerald-500 text-white" onClick={handleAutoGenerate} disabled={autoGenerating}>
                <Wand2 className="w-4 h-4 mr-1" /> Générer depuis la visite technique
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groupedByProject.map((group) => {
            const groupKey = group.project_id || '_no_project';
            const isCollapsed = collapsedGroups[groupKey];
            return (
              <div key={groupKey} className="space-y-2" data-testid={`bdc-group-${groupKey}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(groupKey)}
                  className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-gradient-to-r from-zinc-900 to-zinc-900/60 border border-zinc-800 hover:border-zinc-700 transition-colors text-left"
                  data-testid={`bdc-group-header-${groupKey}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {isCollapsed ? <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                    <Building2 className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{group.title}</div>
                      {group.city && <div className="text-[11px] text-zinc-500">{group.city}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[11px] text-zinc-500">{group.items.length} BDC</span>
                    <span className="text-sm font-mono text-emerald-400">{fmt(group.total_ttc)} € TTC</span>
                  </div>
                </button>
                {!isCollapsed && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pl-4">
                    {group.items.map((b) => {
                      const cfg = STATUS_CONFIG[b.status] || STATUS_CONFIG.a_remplir_artisan;
                      return (
                        <Card key={b.id} className="bg-zinc-900 border-zinc-800 hover:border-zinc-600 transition-colors cursor-pointer" onClick={() => setSelected(b)} data-testid={`bdc-card-${b.id}`}>
                          <CardContent className="p-4 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-white truncate">{b.corps_metier}</div>
                              </div>
                              <Badge className={`border text-[10px] ${cfg.bg}`}>{cfg.label}</Badge>
                            </div>
                            {b.artisan_name && (
                              <div className="text-[11px] text-zinc-400 flex items-center gap-1"><User className="w-3 h-3" /> {b.artisan_name}</div>
                            )}
                            <div className="flex items-center justify-between text-xs pt-1 border-t border-zinc-800">
                              <span className="text-zinc-500">{(b.items || []).length + (b.fournitures || []).length} lignes</span>
                              <span className="font-mono text-emerald-400">{fmt(b.total_ttc || 0)} € TTC</span>
                            </div>
                            {Number(b.visit_estimate) > 0 && Number(b.total_ht) > 0 && (() => {
                              const est = Number(b.visit_estimate);
                              const diff = (Number(b.total_ht) || 0) - est;
                              const pct = (diff / est) * 100;
                              let color, dot;
                              if (Math.abs(pct) <= 10) { color = 'text-emerald-400'; dot = 'bg-emerald-500'; }
                              else if (pct > 10 && pct <= 25) { color = 'text-amber-400'; dot = 'bg-amber-500'; }
                              else { color = 'text-red-400'; dot = 'bg-red-500'; }
                              return (
                                <div className="flex items-center gap-1.5 text-[10px] font-mono" data-testid={`bdc-card-variance-${b.id}`} title={`Visite estimée : ${fmt(est)} € HT`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${dot}`}></span>
                                  <span className={color}>vs visite : {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%</span>
                                </div>
                              );
                            })()}
                            <div className="flex gap-1 pt-1">
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-zinc-400 hover:text-white flex-1" onClick={(e) => { e.stopPropagation(); setSelected(b); }}>
                                Ouvrir
                              </Button>
                              {!isArtisan && (
                                <ConfirmButton
                                  title="Supprimer ce bon de commande ?"
                                  description={`« ${b.corps_metier} » sera définitivement supprimé.`}
                                  confirmLabel="Supprimer"
                                  onConfirm={() => handleDelete(b.id)}
                                  trigger={
                                    <Button size="sm" variant="ghost" className="h-7 text-zinc-500 hover:text-red-400" onClick={(e) => e.stopPropagation()} data-testid={`bdc-delete-${b.id}`}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  }
                                />
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <BdcDetail
          bdc={selected}
          artisans={artisans}
          isArtisan={isArtisan}
          onClose={() => setSelected(null)}
          onChanged={fetchAll}
        />
      )}
    </div>
  );
};
